import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { useRect, useWindowOn } from 'react-use-rect';
import type { PropsWithClassName } from '../../../../../types';
import {
  getPlayerControls,
  getWindowHeight,
  useIsWaveformProgress
} from '../../../../helpers';
import { Buttons } from '../../../buttons';
import { Description, Image, StyledFreeLink, TrackInfo } from '../../../common';
import { useSource } from '../../../sources';
import { usePlayer } from '../../PlayerProvider';
import {
  CloseButton,
  HoverTime,
  MinimizeButton,
  ProgressBar,
  SmallPlayControls,
  Time,
  Track,
  Waveform
} from '../../player-parts';
import { Playlist } from '../../playlist';
import { useFloating } from '../floating';

type ProgressBarPosition = 'top' | 'bottom';

interface SmallProps extends PropsWithClassName {
  hidePlaylist?: boolean;
  progressBarPosition?: ProgressBarPosition;
  isFloating?: boolean;
  isSmallBar?: boolean;
  showControlsInOneLine?: boolean;
}

export function Small({
  hidePlaylist,
  progressBarPosition,
  className,
  showControlsInOneLine,
  isFloating = false,
  isSmallBar = false
}: SmallProps) {
  const [containerWidth, setContainerWidth] = useState(0);
  const { setHeight } = useFloating();
  const [containerRef] = useRect(
    ({ width, height }) => {
      setContainerWidth(width);
      setHeight?.(height);
    },
    {
      resize: true
    }
  );
  const { currentItem } = useSource();
  const {
    playerParts,
    playlistHeight: settingsPlaylistHeight,
    sourceType,
    coverImage,
    closeButton,
    controls: derivedControls,
    existingPlayerControls,
    playerControls,
    minimizeButton
  } = useSettings();
  const [playlistHeight, setPlaylistHeight] = useState(0);
  const [componentRef, revalidate] = useRect(({ height: componentHeight }) =>
    setPlaylistHeight(getWindowHeight() - componentHeight - 24)
  );

  useWindowOn('resize', () => revalidate({ force: true }));

  const { image, title, description } = currentItem ?? {};

  const calculatedMaxHeight = useMemo(() => {
    const playlistHeightString = settingsPlaylistHeight
      .toString()
      .toLowerCase();
    const playlistHeightNumber = parseInt(playlistHeightString, 10);

    const isAutoHeight = ['100%', 'auto'].includes(playlistHeightString);

    if (isAutoHeight) {
      return playlistHeight;
    }

    if (isFloating) {
      return Math.min(playlistHeightNumber, playlistHeight);
    }

    return playlistHeightNumber;
  }, [settingsPlaylistHeight, playlistHeight, isFloating]);

  const controls = getPlayerControls(
    derivedControls,
    existingPlayerControls,
    playerControls
  );

  const { showPlayer } = usePlayer();
  const isWaveformProgress = useIsWaveformProgress();

  if (!showPlayer) {
    return null;
  }

  const minimizeOrCloseBtn = closeButton || minimizeButton;

  const withInfoPanel =
    !playerParts.includes('image') &&
    !playerParts.includes('title') &&
    !playerParts.includes('author');

  const withTopPadding = minimizeOrCloseBtn && withInfoPanel && !isSmallBar;

  const floatingButtonStyleNoSmallBar = withTopPadding
    ? `
        right: 12px;
        top: 12px;
      `
    : undefined;
  const floatingButtonStyle = isSmallBar
    ? `
        right: 12px;
        top: 14px;
      `
    : floatingButtonStyleNoSmallBar;

  const { url } = coverImage ?? {};

  const withFreeLink = showPlayer && isFloating && !minimizeButton;
  const withCloseButton =
    closeButton &&
    !minimizeButton &&
    (!isSmallBar || (sourceType !== 'player' && isSmallBar));
  const withMinimizeButton = minimizeButton && isFloating && !isSmallBar;

  return (
    <Container
      className={className}
      ref={containerRef}
      _withBoxShadow={isFloating}
      _withTopPadding={withTopPadding}
    >
      {withCloseButton && <CloseButton styles={floatingButtonStyle} _float />}
      {withMinimizeButton && (
        <MinimizeButton styles={floatingButtonStyle} floating />
      )}
      <Component ref={componentRef}>
        {!withInfoPanel && (
          <InfoContainer>
            {playerParts.includes('image') && (
              <ImageContainer
                trackName={title}
                link={url || image}
                _size={sourceType === 'rss' ? 84 : 48}
              />
            )}
            <StyledTrackInfo _withPadding={minimizeOrCloseBtn} />
          </InfoContainer>
        )}

        {playerParts.includes('description') && !!description && (
          <Description text={description} />
        )}

        {controls.includes('progressBar') && (
          <Show
            when={isWaveformProgress}
            fallback={
              <StyledProgressBar _progressBarPosition={progressBarPosition} />
            }
          >
            <Waveform />
          </Show>
        )}

        <SmallPlayControls forceOneLine={showControlsInOneLine} />

        {!!currentItem?.buttons?.length && (
          <ButtonWrapper>
            <Buttons containerWidth={containerWidth} />
          </ButtonWrapper>
        )}
      </Component>

      {!hidePlaylist && (
        <StyledPlaylist maxHeight={calculatedMaxHeight} hideDirection="toTop" />
      )}

      {withFreeLink && <StyledFreeLink />}
    </Container>
  );
}

const Container = styled.div<{
  _withBoxShadow: boolean;
  _withTopPadding?: boolean;
}>`
  width: 100%;
  position: relative;
  ${({ _withBoxShadow }) =>
    _withBoxShadow && 'box-shadow: 0 2px 15px rgba(0, 0, 0, 0.15)'}
  ${({ _withTopPadding }) => _withTopPadding && 'padding-top: 44px;'}
`;

const Component = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
  gap: 12px;
`;

const StyledProgressBar = styled(ProgressBar)<{
  _progressBarPosition?: ProgressBarPosition;
}>`
  height: 26px;
  display: flex;
  align-items: flex-end;
  margin-bottom: 6px;
  z-index: 1;
  ${({ _progressBarPosition }) => `
    position: ${_progressBarPosition === 'top' ? 'absolute' : ''};
    top: ${_progressBarPosition === 'top' ? '-16px' : ''};
    width: ${_progressBarPosition === 'top' ? '100%' : ''};
    left: ${_progressBarPosition === 'top' ? '0' : ''};
    background-color: ${_progressBarPosition === 'top' ? 'transparent' : ''};
  `}

  & ${Track} {
    display: flex;
    align-items: flex-end;
  }

  & ${Time} {
    display: ${({ _progressBarPosition }) =>
      _progressBarPosition === 'top' ? 'none' : ''};
    top: -6px;
  }

  & ${HoverTime} {
    margin-top: 0;
    transform: translate(-50%, -50%);
  }
`;

const StyledPlaylist = styled(Playlist)`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: -6px;
  padding: 0 6px;
`;

const InfoContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 1;
`;

const StyledTrackInfo = styled(TrackInfo)<{
  _withPadding?: boolean;
}>`
  ${({ _withPadding }) => _withPadding && 'padding-right: 20px;'}
`;

const ImageContainer = styled(Image)<{
  _size: number;
}>`
  position: relative;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  margin-right: 12px;
  width: ${({ _size }) => _size}px;
  height: ${({ _size }) => _size}px;
`;

const ButtonWrapper = styled.div`
  margin-top: 12px;
  width: 100%;
`;
